import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ArrowLeft, Facebook, Twitter, Share2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsDetailPage = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, [id]);

  const fetchNews = async () => {
    try {
      const [newsRes, allNewsRes] = await Promise.all([
        axios.get(`${API}/news/${id}`),
        axios.get(`${API}/news?limit=4`),
      ]);
      setNews(newsRes.data);
      setRelatedNews(allNewsRes.data.filter(n => n.id !== id).slice(0, 3));
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-gray-400">A carregar...</div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Notícia não encontrada</p>
          <Link to="/noticias" className="btn-secondary">
            Voltar às Notícias
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="news-detail-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-end overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${news.image_url || 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80'}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-transparent" />
        <div className="relative z-10 section-padding pb-12 container-custom">
          <Link 
            to="/noticias" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
            data-testid="back-to-news"
          >
            <ArrowLeft size={16} />
            <span className="text-xs uppercase tracking-widest font-bold">Voltar às Notícias</span>
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <span className="text-xs uppercase tracking-widest text-gray-400 bg-white/10 px-3 py-1">
              {news.category}
            </span>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Calendar size={14} />
              <span>{new Date(news.created_at).toLocaleDateString('pt-PT', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}</span>
            </div>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black max-w-4xl">
            {news.title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Article */}
            <div className="lg:col-span-2">
              <article className="prose prose-invert prose-lg max-w-none">
                <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                  {news.content}
                </div>
              </article>

              {/* Share */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-4">Partilhar</p>
                <div className="flex gap-3">
                  <button className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all">
                    <Facebook size={18} />
                  </button>
                  <button className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all">
                    <Twitter size={18} />
                  </button>
                  <button className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all">
                    <Share2 size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <div className="sticky top-32">
                <h3 className="font-heading text-xl font-bold mb-6">OUTRAS NOTÍCIAS</h3>
                <div className="space-y-4">
                  {relatedNews.map((item) => (
                    <Link
                      key={item.id}
                      to={`/noticias/${item.id}`}
                      className="block group"
                    >
                      <div className="flex gap-4">
                        <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-[#0A0A0A]">
                          <img
                            src={item.image_url || 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80'}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">
                            {new Date(item.created_at).toLocaleDateString('pt-PT')}
                          </p>
                          <h4 className="font-heading font-bold text-sm group-hover:text-gray-300 transition-colors line-clamp-2">
                            {item.title}
                          </h4>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                {relatedNews.length === 0 && (
                  <p className="text-gray-500 text-sm">Sem outras notícias</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetailPage;
